import { useEffect, useRef, useState } from 'react';
import { Box, TextButton } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';

export interface TruncatedUrlDisplayProps {
  /** The URL to display with potential truncation */
  url: string;
  /** The text to display (if different from url) */
  displayText?: string;
  /** Callback when the URL is clicked */
  onClick: () => void;
  /** Maximum number of lines before truncation (default: 5) */
  maxLines?: number;
  /** Average characters per line for estimation (default: 50) */
  charsPerLine?: number;
  /** Custom styling for the container */
  containerCss?: Record<string, unknown>;
  /** Custom styling for the URL button */
  urlButtonCss?: Record<string, unknown>;
}

/**
 * A reusable component that displays a URL with automatic truncation after a specified number of lines.
 * Shows expand/collapse controls only when the URL exceeds the line limit.
 *
 * @example
 * ```tsx
 * <TruncatedUrlDisplay
 *   url="https://very-long-url.com/..."
 *   onClick={() => window.open(url, '_blank')}
 *   maxLines={3}
 * />
 * ```
 */
export function TruncatedUrlDisplay({
  url,
  displayText,
  onClick,
  maxLines = 5,
  charsPerLine = 50,
  containerCss = {},
  urlButtonCss = {},
}: TruncatedUrlDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = useRef<HTMLButtonElement>(null);
  const [shouldShowControls, setShouldShowControls] = useState(false);

  const textToDisplay = displayText || url;

  useEffect(() => {
    // Simple heuristic: if display text is longer than estimated character count, show controls
    // This is more reliable than trying to measure DOM elements
    const estimatedLines = Math.ceil(textToDisplay.length / charsPerLine);
    setShouldShowControls(estimatedLines > maxLines);

    // Reset expanded state when URL changes
    setIsExpanded(false);
  }, [textToDisplay, maxLines, charsPerLine]);

  // Line-based truncation styles using Panda CSS
  const truncationStyles = css.raw({
    lineClamp: maxLines,
  });

  const defaultContainerCss = {
    marginBottom: 'default',
    padding: 'default',
    backgroundColor: 'bg.action.neutral.alt.default',
    borderRadius: 'md',
    width: { base: '274px', md: '470px' },
    maxWidth: { base: 'calc(100vw - 64px)', md: 'min(470px, calc(100vw - 96px))' },
    minWidth: 0,
    maxHeight: isExpanded ? '200px' : 'auto',
    overflow: isExpanded ? 'auto' : 'visible',
    ...containerCss,
  } as const;

  const defaultUrlButtonCss = {
    textDecoration: 'underline',
    wordBreak: 'break-all' as const,
    whiteSpace: 'pre-wrap' as const,
    textAlign: 'center' as const,
    width: '100%',
    minWidth: 0,
    overflowWrap: 'break-word' as const,
    ...urlButtonCss,
  };

  return (
    <Box css={defaultContainerCss}>
      <TextButton
        ref={textRef}
        tone="brand"
        onClick={onClick}
        css={{
          ...defaultUrlButtonCss,
          ...(!isExpanded && shouldShowControls && truncationStyles),
        }}
      >
        {textToDisplay}
      </TextButton>
      {shouldShowControls && (
        <Box css={{ marginTop: 'tighter', textAlign: 'center' }}>
          <TextButton tone="neutral" onClick={() => setIsExpanded(!isExpanded)} css={{ fontSize: 'sm' }}>
            {isExpanded ? 'Show less' : 'Show full URL'}
          </TextButton>
        </Box>
      )}
    </Box>
  );
}

export default TruncatedUrlDisplay;
